import { useEffect } from "react";
import { signOut, type User } from "firebase/auth";
import { PAYMENTS_ENABLED } from "../config/payments";
import { getFirebaseAuth } from "../lib/firebase";
import {
  GUEST_IDLE_CHECK_MS,
  clearGuestActivity,
  clearRetirementPending,
  credentialedRecently,
  gateUnclaimed,
  guestRotationSettled,
  hasLocalGuestClaims,
  isGuestIdle,
  markRetirementPending,
  noteGuestIdentity,
  noteRetirementWaiting,
  pendingRetirementIdentity,
  onGuestSessionChange,
  recordCredentialedIdentity,
  retirementDeferredByCheckout,
  trackGuestRotation,
  whenAllUnclaimed,
} from "../lib/guestSession";

/**
 * RETIRE AN ANONYMOUS GUEST IDENTITY (security audit M1; see lib/guestSession.ts).
 *
 * Signs out, and only when ALL of these hold at the moment of signing out:
 *
 *  - the current Firebase user is ANONYMOUS. A credentialed account is never
 *    signed out here, whatever the caller believed;
 *  - it is the UID the caller named, so a rotation decided about one identity
 *    can never retire a different one that replaced it in between;
 *  - no tab has seen a credentialed identity within the cross-tab sync window
 *    (F4): this tab's user may be a stale copy of one just linked elsewhere;
 *  - nothing on this origin claims the identity (a live hold, a commit in
 *    flight, an unknown outcome, a guest code or proof);
 *  - for `idleOnly`, it is still idle when the decision is made.
 *
 * Signing out, not deleting: the UID remains the booking's `clientId`
 * server-side and the booking remains reachable through its management link.
 * The next hold mints a fresh anonymous identity on demand.
 *
 * `durable` (after an outcome): a refusal because something CLAIMS the
 * identity, or because the sync window has not passed, is not the end of it.
 * The retirement is recorded as pending and retried the moment nothing claims
 * it (`retireWhenUnclaimed`), or by the next idle check, for which a pending
 * retirement is an idle limit of zero.
 *
 * Never throws; resolves whether it rotated now.
 */
export function rotateGuestIdentity(
  expectedUid: string,
  options: { idleOnly?: boolean; settle?: () => Promise<void>; durable?: boolean } = {}
): Promise<boolean> {
  const previous = guestRotationSettled();
  const run = (async () => {
    await previous;
    if (options.settle) await options.settle().catch(() => undefined);
    let checkoutInFlight = false;
    const gate = await gateUnclaimed(async () => {
      // A durable retirement honours a live checkout marker from its FIRST
      // attempt (checkout design §2.3): the checkout tab has already left for
      // Stripe, so no claim or lock speaks for it. It is recorded and waits.
      if (PAYMENTS_ENABLED && options.durable && retirementDeferredByCheckout(currentIdentity(expectedUid))) {
        checkoutInFlight = true;
        return false;
      }
      return retireIfStill(expectedUid, options.idleOnly === true);
    });
    if (gate.ran && gate.result) return true;
    // Deferred, not answered: a claim, a checkout in flight, or a credentialed
    // identity seen inside the sync window (which may yet prove to be this
    // one, linked elsewhere).
    if (options.durable && (!gate.ran || checkoutInFlight || credentialedRecently())) retireWhenUnclaimed(expectedUid);
    return false;
  })().catch(() => false);
  return trackGuestRotation(run);
}

/** Runs inside the lock: re-reads everything the decision rested on. */
async function retireIfStill(expectedUid: string, idleOnly: boolean): Promise<boolean> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user || !user.isAnonymous || user.uid !== expectedUid) return false;
  if (idleOnly && !isGuestIdle(signedInAt(user), Date.now(), createdAt(user))) return false;
  if (credentialedRecently()) return false;
  clearGuestActivity();
  await signOut(auth);
  clearRetirementPending();
  return true;
}

/** The UIDs this tab is already waiting to retire. In memory only. */
const waiting = new Set<string>();

/**
 * A REFUSED AFTER-OUTCOME RETIREMENT, MADE DURABLE (security audit F1).
 *
 * Recorded for every tab (no UID is stored), then retried here with a
 * WAITING exclusive lock: granted once every claim on the origin has ended,
 * and re-checked inside the lock (still anonymous, still this UID, nothing
 * credentialed seen in the sync window) before signing out. If this tab closes first, the flag remains and
 * every other tab's idle check treats it as an idle limit of zero.
 */
function retireWhenUnclaimed(expectedUid: string): void {
  const current = getFirebaseAuth().currentUser;
  markRetirementPending(Date.now(), current?.uid === expectedUid ? createdAt(current) : null);
  if (waiting.has(expectedUid)) return;
  waiting.add(expectedUid);
  // Linking is refused while this waits, even if storage kept no flag (N3).
  noteRetirementWaiting(1);
  // Not the flag: storage may have refused it, and a changed identity says
  // everything the flag would (retired, replaced, or linked to an account).
  const wanted = () => {
    const user = getFirebaseAuth().currentUser;
    return user !== null && user.isAnonymous && user.uid === expectedUid;
  };
  void whenAllUnclaimed(() => trackGuestRotation(retireIfStill(expectedUid, false)), wanted)
    .catch(() => false)
    .finally(() => {
      waiting.delete(expectedUid);
      noteRetirementWaiting(-1);
    });
}

/** The creation instant of the current user, when it is the anonymous `uid`. */
function currentIdentity(uid: string): number | null {
  const user = getFirebaseAuth().currentUser;
  return user && user.isAnonymous && user.uid === uid ? createdAt(user) : null;
}

/** When the identity was created, or null when Firebase does not say. */
function createdAt(user: Partial<Pick<User, "metadata">>): number | null {
  const at = user.metadata?.creationTime ? Date.parse(user.metadata.creationTime) : Number.NaN;
  return Number.isFinite(at) ? Math.floor(at) : null;
}

/** The anonymous sign-in instant, or null when Firebase does not say. */
export function signedInAt(user: Pick<User, "metadata">): number | null {
  const raw = user.metadata?.lastSignInTime ?? user.metadata?.creationTime;
  const at = raw ? Date.parse(raw) : Number.NaN;
  return Number.isFinite(at) ? at : null;
}

/**
 * What IdentityProvider tells rotation about each identity change, from the
 * Firebase listener itself rather than a later effect:
 *
 *  - a credentialed identity is current: stamp it, so no tab signs out what
 *    may be a stale copy of it (F4); and nothing is pending any more, because
 *    the identity that booked is either this account now or gone;
 *  - no identity at all, or an anonymous one created at a different instant
 *    than the one the retirement is pending for: it already happened.
 */
export function noteIdentityChange(user: (Pick<User, "isAnonymous"> & Partial<Pick<User, "metadata">>) | null): void {
  // Which identity this tab's claim ends are published for, and read for (IF-1).
  noteGuestIdentity(user && user.isAnonymous ? createdAt(user) : null);
  if (user && !user.isAnonymous) {
    recordCredentialedIdentity();
    clearRetirementPending();
  } else if (!user) {
    clearRetirementPending();
  } else {
    // An anonymous identity. Which one this tab saw before proves nothing
    // (N7): a tab frozen in the background or restored from the bfcache may
    // have last seen an older one, and waking to the PENDING identity must
    // not clear its retirement, or any tab could then link it. Cleared only
    // when the flag names its identity and this one was created at a
    // different instant (the device holds one identity, so the pending one is
    // gone); otherwise `ensureGuestIdentity` clears it when it mints.
    const pendingFor = pendingRetirementIdentity();
    const created = createdAt(user);
    if (pendingFor !== null && created !== null && created !== pendingFor) clearRetirementPending();
  }
}

/**
 * ROTATE AN IDLE, UNCLAIMED ANONYMOUS IDENTITY.
 *
 * Checked when the identity is restored or changes (a cold start on a shared
 * device is where an abandoned identity is found), every
 * `GUEST_IDLE_CHECK_MS` while the page is open, when a hidden tab returns
 * (phones stop timers in the background), and whenever this tab's last claim
 * ends or any tab records a pending retirement, which makes the limit zero.
 */
export function useIdleGuestRotation(user: User | null): void {
  useEffect(() => {
    if (!user || !user.isAnonymous) return;
    const uid = user.uid;
    const since = signedInAt(user);
    const created = createdAt(user);
    const check = () => {
      if (hasLocalGuestClaims() || !isGuestIdle(since, Date.now(), created)) return;
      void rotateGuestIdentity(uid, { idleOnly: true });
    };
    check();
    const timer = window.setInterval(check, GUEST_IDLE_CHECK_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    const unsubscribe = onGuestSessionChange(check);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribe();
    };
  }, [user]);
}

/**
 * ROTATE ONCE A DEFINITE OUTCOME IS ON SCREEN.
 *
 * `outcomeUid` is the UID the CONFIRMED or REQUESTED booking was made under,
 * non-null only while that outcome is rendered. Runs from an effect, so after
 * the outcome and its management link have committed to the DOM; never from
 * render. `settle` lets the caller finish work the old UID still has to
 * authorize first (releasing an unconsumed hold). Idempotent: a second run
 * finds the UID already gone and does nothing. Refused by a claim, it becomes
 * a pending retirement rather than being dropped.
 */
export function useRotateAfterOutcome(outcomeUid: string | null, settle?: () => Promise<void>): void {
  useEffect(() => {
    if (!outcomeUid) return;
    void rotateGuestIdentity(outcomeUid, { settle, durable: true });
  }, [outcomeUid, settle]);
}

/** Test-only. */
export function __resetGuestRotationForTests(): void {
  waiting.clear();
}
